import * as Yup from "yup";
import {
  FieldDatabaseType,
  FieldDisplayType,
  StandardFieldDisplayType,
  FileFieldDisplayType,
  RelationshipFieldDatabaseType,
  isRelationshipFieldType,
  isRelationshipOrFileOrUserFieldType,
} from "@/types/breeze/Field";
import { inputValidationSchema } from "@/components/data-model/InputDisplayOptions";
import { selectValidationSchema } from "@/components/data-model/SelectDisplayOptions";
import { textAreaValidationSchema } from "@/components/data-model/TextAreaDisplayOptions";
import { richTextValidationSchema } from "@/components/data-model/RichTextDisplayOptions";
import { fileInputValidationSchema } from "@/components/data-model/FileInputDisplayOptions";

// Base validation that's always required
const baseValidationSchema = Yup.object().shape({
  name: Yup.string()
    .required("This field is required.")
    .matches(
      /^[a-zA-Z0-9_]+$/,
      "Must contain only letters, numbers, and underscores and cannot start with an underscore or a number",
    )
    .matches(/^(?![_0-9])/, "Cannot start with an underscore or a number"),
  database_type: Yup.string().required("This field is required."),
  display_type: Yup.string().required("This field is required."),
  description: Yup.string().nullable(),
  is_required: Yup.boolean(),
  default_value: Yup.string().nullable(),
});

// Display type validation - based on how the field is displayed
function getDisplayTypeValidation(displayType: FieldDisplayType) {
  switch (displayType) {
    case StandardFieldDisplayType.INPUT:
    case StandardFieldDisplayType.SHORT_DATE:
    case StandardFieldDisplayType.LONG_DATE:
    case StandardFieldDisplayType.TIMESTAMP:
      return Yup.object().shape(inputValidationSchema);
    case StandardFieldDisplayType.SELECT:
      return Yup.object().shape(selectValidationSchema);
    case StandardFieldDisplayType.TEXTAREA:
      return Yup.object().shape(textAreaValidationSchema);
    case StandardFieldDisplayType.RICH_TEXT:
      return Yup.object().shape(richTextValidationSchema);
    case FileFieldDisplayType.FILE:
      return Yup.object().shape(fileInputValidationSchema);
    default:
      return Yup.object().shape({
        options: Yup.object().nullable(),
      });
  }
}

// Database type validation - based on data storage type (mainly for relationships)
function getDatabaseTypeValidation(
  databaseType: FieldDatabaseType,
  isRequired?: boolean,
) {
  const relationshipValidation: Record<string, any> = {};

  if (isRelationshipFieldType(databaseType)) {
    // Full relationship fields need target entity and deletion behavior
    relationshipValidation.relationship = Yup.object()
      .shape({
        target_entity_id: Yup.number().required("Related entity is required"),
        target_field_name:
          databaseType === RelationshipFieldDatabaseType.ONE_TO_MANY
            ? Yup.string().required("Foreign key is required")
            : Yup.string().nullable(),
        on_deletion: Yup.string()
          .oneOf(["CASCADE", "SET NULL", "SET DEFAULT", "RESTRICT"])
          .required("On deletion behavior is required")
          .test(
            "required-field-cannot-set-null",
            "Required fields cannot use SET NULL",
            (value) => !isRequired || value !== "SET NULL",
          ),
      })
      .required("Relationship configuration is required");
  } else if (isRelationshipOrFileOrUserFieldType(databaseType)) {
    // File and user fields only need deletion behavior
    relationshipValidation.relationship = Yup.object()
      .shape({
        on_deletion: Yup.string()
          .oneOf(["CASCADE", "SET NULL", "SET DEFAULT", "RESTRICT"])
          .required("On deletion behavior is required")
          .test(
            "required-field-cannot-set-null",
            "Required fields cannot use SET NULL",
            (value) => !isRequired || value !== "SET NULL",
          ),
      })
      .required("Deletion behavior is required");
  } else {
    // Non-relationship fields don't need relationship config
    relationshipValidation.relationship = Yup.object().nullable();
  }

  return Yup.object().shape(relationshipValidation);
}

// Main validation builder that combines all validation concerns
export function buildFieldValidationSchema(
  displayType: FieldDisplayType,
  databaseType: FieldDatabaseType,
  isRequired?: boolean,
) {
  const displayValidation = getDisplayTypeValidation(displayType);
  const databaseValidation = getDatabaseTypeValidation(
    databaseType,
    isRequired,
  );

  // Combine all validations
  return baseValidationSchema
    .concat(displayValidation)
    .concat(databaseValidation);
}

// Convenience function for getting just the options validation (for existing components)
export function getDisplayOptionsValidation(displayType: FieldDisplayType) {
  const validation = getDisplayTypeValidation(displayType);
  return validation.fields.options || Yup.object().nullable();
}
